import { actionButton, createElement, labeledField, replaceChildren, toText } from './dom.js';
import { renderItem } from './item-renderers.js';

function clampProgress(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.min(100, Math.round(numeric))) : 0;
}

function effectiveItems(chapters) {
  return chapters.filter(chapter => chapter.selected).flatMap(chapter =>
    (chapter.items || []).filter(item => item.selected)
  );
}

function summaryStat(label, value, hint, extra) {
  return createElement('div', { className: 'summary-stat' }, [
    createElement('span', { text: label }),
    createElement('strong', { text: value }),
    extra || createElement('small', { text: hint })
  ]);
}

function progressBar(progress) {
  const fill = createElement('i');
  fill.style.width = `${progress}%`;
  return createElement('div', {
    className: 'linear-progress',
    attrs: {
      role: 'progressbar',
      'aria-label': `项目完成度 ${progress}%`,
      'aria-valuemin': '0',
      'aria-valuemax': '100',
      'aria-valuenow': String(progress)
    }
  }, [fill]);
}

export function renderSummary(root, state) {
  const chapters = state.document.chapters || [];
  const items = effectiveItems(chapters);
  const images = items.filter(item => item.type === 'image');
  const progress = clampProgress(state.document.project?.progress);
  const selectedChapters = chapters.filter(chapter => chapter.selected).length;
  replaceChildren(root, [
    summaryStat('项目完成度', `${progress}%`, '', progressBar(progress)),
    summaryStat('成果章节', `${selectedChapters} / ${chapters.length} 章`, '按当前编排顺序'),
    summaryStat('已选内容', `${items.length} 项`, '只统计已选章节中的成果'),
    summaryStat('图像成果', `${images.length} 张`, '导出时读取真实图片文件')
  ]);
}

export function updateShellProject(state) {
  const project = state.document.project || {};
  const projectName = toText(project.name, '未命名项目');
  const progress = clampProgress(project.progress);
  const completed = Math.min(6, Math.round((progress / 100) * 6));
  for (const id of ['projectName', 'sidebarProjectName']) {
    const node = document.getElementById(id);
    if (node) node.textContent = projectName;
  }
  const progressRing = document.getElementById('projectProgressRing');
  const progressText = document.getElementById('projectProgressText');
  const progressHint = document.getElementById('projectProgressHint');
  const autosave = document.getElementById('autosaveLabel');
  if (progressRing) progressRing.style.setProperty('--p', String(progress));
  if (progressText) progressText.textContent = `${progress}%`;
  if (progressHint) progressHint.textContent = `${completed} / 6 阶段完成`;
  if (autosave) autosave.textContent = state.revision ? `草稿已保存，修订 ${state.revision}` : '草稿已保存';
}

export function renderRail(root, chapters, activeChapterId, onActivate) {
  const buttons = chapters.map((chapter, index) => {
    const selected = (chapter.items || []).filter(item => item.selected).length;
    const button = createElement('button', {
      className: `chapter-nav-item${chapter.id === activeChapterId ? ' active' : ''}`,
      attrs: {
        type: 'button',
        'aria-current': chapter.id === activeChapterId ? 'true' : null,
        'aria-label': `前往第 ${index + 1} 章：${toText(chapter.title, '未命名章节')}`
      },
      dataset: { chapterId: chapter.id }
    }, [
      createElement('span', { className: 'chapter-nav-index', text: String(index + 1).padStart(2, '0') }),
      createElement('span', { className: 'chapter-nav-title', text: toText(chapter.title, '未命名章节') }),
      createElement('span', { className: 'chapter-nav-count', text: `${selected}/${chapter.items?.length || 0}` })
    ]);
    button.addEventListener('click', () => onActivate?.(chapter.id));
    return button;
  });
  replaceChildren(root, buttons);
}

function chapterMoveControls(chapter, index, total, onMove) {
  const up = actionButton({
    label: `上移章节：${toText(chapter.title, '未命名章节')}`,
    icon: 'i-arrow',
    className: 'icon-btn move-button move-up',
    disabled: index === 0
  });
  const down = actionButton({
    label: `下移章节：${toText(chapter.title, '未命名章节')}`,
    icon: 'i-arrow',
    className: 'icon-btn move-button move-down',
    disabled: index === total - 1
  });
  up.addEventListener('click', () => onMove?.(chapter.id, -1));
  down.addEventListener('click', () => onMove?.(chapter.id, 1));
  return createElement('div', { className: 'chapter-card-actions' }, [up, down]);
}

function chapterHeader(chapter, index, total, callbacks) {
  const checkbox = createElement('input', {
    className: 'selection-check',
    attrs: { type: 'checkbox', 'aria-label': `选择章节：${toText(chapter.title, '未命名章节')}` }
  });
  checkbox.checked = Boolean(chapter.selected);
  checkbox.addEventListener('change', () => callbacks.onSelectChapter?.(chapter.id, checkbox.checked));
  const selected = (chapter.items || []).filter(item => item.selected).length;
  return createElement('header', { className: 'chapter-card-head' }, [
    checkbox,
    createElement('div', { className: 'chapter-card-title' }, [
      createElement('strong', { text: `${String(index + 1).padStart(2, '0')} ${toText(chapter.title, '未命名章节')}` }),
      createElement('small', { text: `${selected} / ${chapter.items?.length || 0} 项已选择` })
    ]),
    chapterMoveControls(chapter, index, total, callbacks.onMoveChapter)
  ]);
}

function chapterEditor(chapter, onUpdate) {
  return createElement('div', { className: 'field-grid chapter-editor' }, [
    labeledField({
      label: '章节标题',
      value: chapter.title,
      onChange: title => onUpdate?.(chapter.id, { title })
    }),
    labeledField({
      label: '章节补充说明',
      value: chapter.note,
      multiline: true,
      onChange: note => onUpdate?.(chapter.id, { note })
    })
  ]);
}

function renderChapter(chapter, index, total, callbacks) {
  const article = createElement('article', {
    className: 'chapter-card',
    attrs: { 'aria-selected': String(Boolean(chapter.selected)), tabindex: '-1' },
    dataset: { chapterId: chapter.id }
  });
  const body = createElement('div', { className: 'chapter-card-body' }, [
    chapterEditor(chapter, callbacks.onUpdateChapter)
  ]);
  const items = chapter.items || [];
  if (items.length) {
    const itemList = createElement('div', { className: 'chapter-items' });
    replaceChildren(itemList, items.map((item, itemIndex) => renderItem(item, {
      chapterId: chapter.id,
      index: itemIndex,
      total: items.length,
      onSelect: callbacks.onSelectItem,
      onMove: callbacks.onMoveItem,
      onUpdate: callbacks.onUpdateItem,
      onPreview: callbacks.onPreviewImage
    })));
    body.append(itemList);
  } else {
    body.append(createElement('div', {
      className: 'chapter-card-empty',
      text: '当前章节没有符合“已确认、已采用或已保存到成果”条件的内容。'
    }));
  }
  article.append(chapterHeader(chapter, index, total, callbacks), body);
  return article;
}

export function renderChapters(root, chapters, callbacks) {
  const nodes = chapters.map((chapter, index) => renderChapter(chapter, index, chapters.length, callbacks));
  replaceChildren(root, nodes);
  return chapters.map((chapter, index) => ({ chapterId: chapter.id, node: nodes[index] }));
}
